import axios from 'axios';
import qs from 'qs';
import i18n from '@/locales';
import store from '@/store';
import App from '@/main';

const API_URL = process.env.VUE_APP_API;

const logoutMessages = [
    'You must login to access this endpoint',
    'Unauthenticated.',
    'Unauthenticated',
    'Access is denied.',
    'The refresh token is invalid.',
];

/**
 * IIFE that sets up a wrapper for Axios' cancellation
 * API - https://github.com/axios/axios#cancellation
 *
 * It provides an interface to generate cancellation
 * tokens and to cancel further requests. If a request
 * is cancelled, a new token needs to be generated in the
 * HTTP request.
 *
 * As an IIFE, we generate a token when it's invoked that
 * will be used as the default token
 */
let cancelSource;
export const requestCanceller = (() => {
  cancelSource = axios.CancelToken.source();
  return {
    getToken(generateNewSource = false) {
      if (generateNewSource) {
        cancelSource = axios.CancelToken.source();
      }
      return cancelSource.token;
    },
    cancelFurtherRequests(message) {
      // causes the request to throw error with {message}
      cancelSource.cancel(message);
    },
    cancelCurrentRequest(message) {
      // causes the request to throw error with {message}
      cancelSource.cancel(message);
      // generate new source so further requests are not cancelled
      cancelSource = axios.CancelToken.source();
    },
  };
})();

axios.interceptors.request.use(async (config) => {
  if (!config.meta) {
    config.meta = {}; // eslint-disable-line no-param-reassign
  }

  // TODO Please annotate this code if you're making changes

  const isAuthRequest = ['attempt-refresh', 'login', 'logout', 'reset-password', 'forgot-password', 'register'].includes(config.meta.type);
  if (config.meta.authorized && !isAuthRequest) {
    const isInDate = store.getters['auth/isInDate'];
    const isFormattedCorrectly = store.getters['auth/isFormattedCorrectly'];

    if (!isFormattedCorrectly) {
      await store.dispatch('auth/LOGOUT');
      requestCanceller.cancelFurtherRequests('Access token missing or malformed'); // throws exception
    }

    if (!isInDate) {
      await store.dispatch('auth/ATTEMPT_REFRESH', false);

      const newConfig = config;
      newConfig.headers.Authorization = `Bearer ${store.getters['auth/accessToken']}`;
      return newConfig;
    }
  }

  return config;
});

// TODO refactor
axios.interceptors.response.use(response => response, async (error) => {
  const failedPromise = Promise.reject(error);

  /**
   * If the request was cancelled, we go no further
   */
  if (axios.isCancel(error)) {
    return failedPromise;
  }

  if (!error.config) {
    error.config = {}; // eslint-disable-line no-param-reassign
  }
  if (!error.config.meta) {
    error.config.meta = {}; // eslint-disable-line no-param-reassign
  }

  if (error.response?.status === 401) {
    if (error?.response?.data?.multi_factor_auth_authentication_required && App?.$router?.currentRoute?.name !== 'MFA') {
      App.$router.push({ name: 'MFA' });
      return failedPromise;
    }

    if (error?.response?.data?.multi_factor_auth_setup_required && App?.$router?.currentRoute?.name !== 'SetupMFA') {
      App.$router.push({ name: 'SetupMFA' });
      return failedPromise;
    }
  }

  /**
   * Do we want to suppress all errors?
   */
  if (error.config.meta.suppressAllErrors) {
    return failedPromise;
  }

  /**
   * Non-axios/http errors
   */
  if (!error.response) {
    App.$bus.$emit('renderGlobalMessage', {
      message: error.message,
      type: 'error',
      title: i18n.t('functional.error'),
    });
    return failedPromise;
  }

  /**
   * Do we want to suppress all server errors?
   *
   * @TODO - look at the order to improve what we want to handle i.e.
   * specific form validation errors vs all server errors. Could test
   * against status code but may not be consistent (?)
   */
  if (error.config.meta.suppressServerErrors) {
    return failedPromise;
  }

  /**
   * Server errors
   */
  if (error.response.status === 500) {
    App.$bus.$emit('renderGlobalMessage', {
      message: error.response.statusText,
      type: 'error',
      title: i18n.t('functional.error'),
    });
    return failedPromise;
  }

  /**
   * Authentication errors
   */
  // TODO this should use status codes
  const shouldForceLogout = logoutMessages.some(message => error.response.data.message === message);

  if (shouldForceLogout && error.config.meta.type !== 'logout') {
    await store.dispatch('auth/LOGOUT');
  }

  /**
   * Do we want to only suppress form errors?
   */
  if (error.response.data.errors && error.config.meta.suppressFormErrors) {
    return failedPromise;
  }

  /**
   * Validation errors coming back from the server
   */
  if (error.response.data.errors) {
    Object.keys(error.response.data.errors)
      .forEach((key) => {
        error.response.data.errors[key].forEach((validationMessage) => {
          App.$bus.$emit('renderGlobalMessage', {
            title: i18n.t('functional.error'),
            message: `${key}: ${validationMessage}`,
            type: 'error',
          });
        });
      });
    return failedPromise;
  }

  /**
   * A non-validation error from the server
   */
  if (error.response.data && error.response.data.message) {
    let errorMessage = error.response.data.message;

    // Override the horrible scope error message from the server. Respond with nice i18n string
    if (errorMessage === 'Invalid scope(s) provided.') {
      errorMessage = i18n.t('functional.permissionError');
    }
    App.$bus.$emit('renderGlobalMessage', {
      title: i18n.t('functional.error'),
      message: errorMessage,
      type: 'error',
    });
    return failedPromise;
  }

  /**
   * No condition above has been met
   */
  return failedPromise;
});

const HTTP = (options) => {
  const headers = options.headers || {};
  const accessToken = store.getters['auth/accessToken'];
  if (options.auth !== false && accessToken !== null) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return axios({
    method: options.method || 'get',
    baseURL: API_URL,
    url: `/${options.url}`,
    params: options.params,
    responseType: options.responseType || undefined,
    paramsSerializer: function (params) { // eslint-disable-line
      return qs.stringify(params, { arrayFormat: 'indices' });
    },
    headers,
    data: options.data,
    onUploadProgress: options.progressEvent,
    onDownloadProgress: options.onDownloadProgress || undefined,
    /**
     * The cancel token allows us to - at some later point - cancel further requests.
     * Generally we use this to cancel other requests if the user is not authenticated,
     * but it can be used at any time.
     *
     * Axios needs a cancel token to keep track of things, so we use generate and use a
     * default one if there isn't one provided in the options. We usually pass a cancelToken
     * to the options if we want a fresh start - usually after requests have been cancelled,
     * in pages/actions such as logging in or resetting a password.
     */
    cancelToken: options.cancelToken || requestCanceller.getToken(),

    /**
     * Additional options handled for response interceptor
     */
    meta: {
      ...options.meta,
      authorized: typeof options.auth !== 'undefined' ? options.auth : true,
      suppressAllErrors: options.suppressAllErrors || false,
      suppressServerErrors: options.suppressServerErrors || false,
      suppressFormErrors: options.suppressFormErrors || false,
    },
  });
};

export default HTTP;
